import React, { useState } from 'react';
import useModal from '@/hooks/useModal';
import { ModalHeader } from '@/components/modals/ModalHeader';
import { ModalTitle } from './ModalTitle';
import { useQuery } from '@tanstack/react-query';
import {
	actionRequiredService,
	casesService,
	GET_CASE_ID,
	GET_RETAINER_PACKAGE,
} from '@/api';
import {
	ActionRequired,
	ItemDocumentPackage,
	SendFollowUpPayload,
} from '@/interfaces';
import { LoaderScreen } from '@/components/common';
import { RetainerDocument } from './RetainerDocument';
import { GeneralDocument } from './GeneralDocument';
import { useNotification } from '@/hooks/useNotification';
import { useMediaQuery } from 'react-responsive';
import { FollowUp } from './FollowUp';

interface Props {
	isOpen: boolean;
	setIsModalOpen: (value: boolean) => void;
	retainer: ActionRequired | undefined;
	refetch?: () => void;
	isClientAction?: boolean;
}

export const ModalSignRetainer: React.FC<Props> = ({
	isOpen,
	retainer,
	refetch,
	setIsModalOpen,
	isClientAction,
}) => {
	const { toastSuccess } = useNotification();
	const { Modal, show, hide } = useModal();
	const [listDocuments, setListDocuments] =
		React.useState<ItemDocumentPackage[]>();
	const [followUpAnswers, setFollowUpAnswers] = useState<
		Record<number, SendFollowUpPayload>
	>({});
	const [currentIndex, setCurrentIndex] = useState<number>(0);
	const currentDocument = listDocuments?.[currentIndex];
	const hasNextDocument = listDocuments?.[currentIndex + 1];

	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-width: 1024px)',
	});

	const { data: actionDetails } = useQuery({
		queryKey: [GET_RETAINER_PACKAGE, retainer?.id],
		queryFn: () =>
			actionRequiredService.getActionRequiredRelated(retainer?.id ?? 0),
	});

	const caseId = retainer?.url
		? new URLSearchParams(retainer.url.split('?')[1]).get('caseId') ?? undefined
		: undefined;

	const { data: caseDetails } = useQuery({
		queryKey: [GET_CASE_ID, caseId],
		queryFn: () => casesService.getById(caseId as string),
		enabled: !!caseId,
		refetchOnWindowFocus: false,
	});

	const caseType = caseDetails?.data.case_type;

	React.useEffect(() => {
		if (!actionDetails) return;

		const documents: ItemDocumentPackage[] = actionDetails.data
			.map((d) => {
				let type: ItemDocumentPackage['type'] = 'Document';
				if (d.action_type === 'RETAINER') type = 'Retainer';
				else if (d.action_type === 'FOLLOW_UP_DOCUMENT') type = 'Follow-up';
				return {
					id: d.id,
					type,
					name: d.name,
					applicationId: d.application_id,
					isCompleted: d.isCompleted ?? false,
				};
			})
			.filter((d) => !d.isCompleted);

		if (documents.length === 0) {
			handleClose();
			return;
		}

		setListDocuments([...documents]);
	}, [actionDetails, retainer]);

	const updateStatusAction = (status: boolean) => {
		const newDocuments = listDocuments?.map((doc) => {
			if (doc.id === currentDocument?.id) {
				return {
					...doc,
					isCompleted: status,
				};
			}
			return doc;
		});

		setListDocuments(newDocuments);
		return newDocuments;
	};

	const handleCloseModal = (): void => {
		hide();
		setIsModalOpen(false);
	};

	const handleContinue = (isApproved: boolean) => {
		if (!isApproved) {
			refetch?.();
			handleClose();
			return;
		}

		const documents = updateStatusAction(isApproved);

		if (hasNextDocument) {
			setCurrentIndex((prev) => (prev += 1));
		} else {
			const isAllApproved = documents?.every((d) => d.isCompleted);
			if (isAllApproved) {
				toastSuccess('The documents has been signed successfully.');
			}
			handleClose();
		}
	};

	const handleClose = () => {
		setIsModalOpen(false);
		hide();
	};

	React.useEffect(() => {
		if (isOpen) show();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	return (
		<>
			<Modal
				avoidClose
				withXButton
				classNameCardContainer="!bg-gradient-to-r from-white to-white md:w-full max-w-[1220px]  h-[90vh] relative"
				closeIconTopPosition={isDesktopOrLaptop ? '45px' : '30px'}
				title={
					<ModalHeader
						title={
							<ModalTitle
								title={'Review & Sign Retainer Package'}
								current={currentIndex + 1}
								documentName={currentDocument?.name ?? ''}
								total={listDocuments?.length ?? 0}
							/>
						}
						classNameContent="!justify-start"
						classNameImage="!block"
					/>
				}
				onClose={handleCloseModal}
				classNameContainerChildren="min-h-[65vh] !max-h-[78vh] md:h-[calc(90vh-150px)] no-scrollbar-mobile flex flex-col"
				classNameFooter="md:pt-6"
			>
				{!currentDocument ? (
					<LoaderScreen />
				) : (
					<>
						{currentDocument.type === 'Retainer' && (
							<RetainerDocument
								hasNextDocument={!!hasNextDocument}
								onContinue={handleContinue}
								document={currentDocument}
								isClientAction={isClientAction}
								caseType={caseType}
							/>
						)}
						{currentDocument.type === 'Document' && (
							<GeneralDocument
								hasNextDocument={!!hasNextDocument}
								onContinue={handleContinue}
								document={currentDocument}
								isClientAction={isClientAction}
							/>
						)}
						{currentDocument.type === 'Follow-up' && (
							<FollowUp
								id={currentDocument.id}
								hasNextDocument={!!hasNextDocument}
								savedAnswers={followUpAnswers[currentDocument.id]}
								onSaveAnswers={(id, payload) =>
									setFollowUpAnswers((prev) => ({ ...prev, [id]: payload }))
								}
								onContinue={handleContinue}
							/>
						)}
					</>
				)}
			</Modal>
		</>
	);
};
